import React, { useEffect, useMemo, useState } from 'react';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Menu, MenuItem, Stack } from '@mui/material';
import { FormGroup, FormLabel } from '@mui/material';
import { Container, FilterChips } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import { FormProvider, useForm } from 'react-hook-form';
import InputAutocomplete from '@components/InputAutocomplete';
import InputSliderRange from '@components/Input/InputSliderRange';
import { CompaniesFilter } from './hooks/useGetCompanies';
import { ProAvailableFilters } from './hooks/useGetCompanyAvailableFilters';

interface CompanyFilterForm {
    sector: string[];
    postalCode: string[];
    city: string[];
    totalJobOffersRange: [number, number];
}

const defaultCompanyFilterForm: CompanyFilterForm = {
    sector: [],
    postalCode: [],
    city: [],
    totalJobOffersRange: [0, 0],
};

const companyFilterLabels: Record<string, string> = {
    sector: 'Secteur',
    postalCode: 'Code postal',
    city: 'Ville',
    totalJobOffersRange: "Offres d'emploi",
};

const filterToForm = (filter: CompaniesFilter, rangeMin: number, rangeMax: number): CompanyFilterForm => ({
    sector: filter.sector ?? [],
    postalCode: filter.postalCode ?? [],
    city: filter.city ?? [],
    totalJobOffersRange: [
        filter.totalJobOffersMin ?? rangeMin,
        filter.totalJobOffersMax ?? rangeMax,
    ],
});

const formToFilter = (form: CompanyFilterForm, rangeMin: number, rangeMax: number): CompaniesFilter => {
    const result: CompaniesFilter = {};
    if (form.sector?.length) result.sector = form.sector;
    if (form.postalCode?.length) result.postalCode = form.postalCode;
    if (form.city?.length) result.city = form.city;
    if (form.totalJobOffersRange[0] > rangeMin) result.totalJobOffersMin = form.totalJobOffersRange[0];
    if (form.totalJobOffersRange[1] < rangeMax) result.totalJobOffersMax = form.totalJobOffersRange[1];
    return result;
};

export interface CompanyFilterProps {
    totalDocs: number;
    selectedIds: string[];
    isUpdatingFeatured: boolean;
    filter: CompaniesFilter;
    availableFilters: ProAvailableFilters;
    onQueryChange: (value: string) => void;
    onFilterChange: (filter: CompaniesFilter) => void;
    onBulkFeaturedUpdate: (isFeatured: boolean) => void;
    onAddButtonClick: () => void;
}

const CompanyFilter = ({
    totalDocs,
    selectedIds,
    isUpdatingFeatured,
    filter,
    availableFilters,
    onQueryChange,
    onFilterChange,
    onBulkFeaturedUpdate,
    onAddButtonClick
}: CompanyFilterProps) => {
    const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const rangeMin = availableFilters.totalJobOffersRange.min;
    const rangeMax = availableFilters.totalJobOffersRange.max;

    const sectorOptions = useMemo(
        () => availableFilters.sectors.map((s) => ({ label: `${s.label} (${s.count})`, value: s.value })),
        [availableFilters.sectors],
    );

    const postalCodeOptions = useMemo(
        () => availableFilters.postalCodes.map((p) => ({ label: `${p.label} (${p.count})`, value: p.value })),
        [availableFilters.postalCodes],
    );

    const cityOptions = useMemo(
        () => availableFilters.cities.map((c) => ({ label: `${c.label} (${c.count})`, value: c.value })),
        [availableFilters.cities],
    );

    const methods = useForm<CompanyFilterForm>({
        defaultValues: filterToForm(filter, rangeMin, rangeMax),
    });

    useEffect(() => {
        methods.reset(filterToForm(filter, rangeMin, rangeMax));
    }, [filter, rangeMin, rangeMax, methods]);

    const chipLabels = useMemo(
        () => ({
            sector: availableFilters.sectors.map((s) => ({ id: s.value, value: s.value, label: s.label })),
            postalCode: availableFilters.postalCodes.map((p) => ({ id: p.value, value: p.value, label: p.label })),
            city: availableFilters.cities.map((c) => ({ id: c.value, value: c.value, label: c.label })),
        }),
        [availableFilters],
    );

    // Build a chips-safe filter: exclude totalJobOffersRange tuple (renderValue can't handle number primitives)
    // and only inject it as a { min, max } object when the range differs from the global defaults.
    const displayFilter = useMemo(() => {
        const { totalJobOffersRange, ...rest } = filterToForm(filter, rangeMin, rangeMax);
        const [min, max] = totalJobOffersRange;
        const isRangeActive = min !== rangeMin || max !== rangeMax;
        return isRangeActive ? { ...rest, totalJobOffersRange: { min, max } } : rest;
    }, [filter, rangeMin, rangeMax]);

    const hasActiveFilters = useMemo(() => Object.values(filter).some(hasValue), [filter]);

    const toggleDrawer = (open: boolean) => () => setIsDrawerOpen(open);

    const onApplyFilter = (data: CompanyFilterForm) => {
        onFilterChange(formToFilter(data, rangeMin, rangeMax));
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        methods.reset(defaultCompanyFilterForm);
        onFilterChange({});
        setIsDrawerOpen(false);
    };

    const hasSelection = selectedIds.length > 0;

    const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setActionsMenuAnchor(event.currentTarget);
    };

    const handleCloseActionsMenu = () => {
        setActionsMenuAnchor(null);
    };

    return (
        <FormProvider {...methods}>
            <Stack spacing={2} sx={{ mb: 3 }}>
                <Container>
                    <FlexRow sx={{ gap: 2 }}>
                        <SearchBarWithNumber totalCount={totalDocs} onChange={onQueryChange} />
                        <FilterButton toggleDrawer={toggleDrawer} hasActiveFilters={hasActiveFilters} />
                    </FlexRow>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="secondary"
                            endIcon={<KeyboardArrowDownIcon />}
                            onClick={handleOpenActionsMenu}
                            disabled={!hasSelection}>
                            Actions
                        </Button>
                        <Menu
                            anchorEl={actionsMenuAnchor}
                            open={Boolean(actionsMenuAnchor)}
                            onClose={handleCloseActionsMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <MenuItem
                                onClick={() => {
                                    onBulkFeaturedUpdate(true);
                                    handleCloseActionsMenu();
                                }}
                                disabled={!hasSelection || isUpdatingFeatured}>
                                Mettre en vedette
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    onBulkFeaturedUpdate(false);
                                    handleCloseActionsMenu();
                                }}
                                disabled={!hasSelection || isUpdatingFeatured}>
                                Retirer la vedette
                            </MenuItem>
                        </Menu>
                        <Button
                            variant="contained"
                            startIcon={<i className="fi fi-rr-plus" />}
                            onClick={onAddButtonClick}>
                            Ajouter un établissement
                        </Button>
                    </Stack>
                </Container>

                {hasActiveFilters && (
                    <Container>
                        <FlexRow>
                            <FilterChips
                                filter={displayFilter}
                                labels={companyFilterLabels}
                                defaultFilters={{ sector: [], postalCode: [], city: [] }}
                                onFilterChange={(formFilter: any) => {
                                    const range = formFilter.totalJobOffersRange;
                                    onFilterChange(
                                        formToFilter(
                                            {
                                                sector: formFilter.sector ?? [],
                                                postalCode: formFilter.postalCode ?? [],
                                                city: formFilter.city ?? [],
                                                totalJobOffersRange: range
                                                    ? [range.min, range.max]
                                                    : [rangeMin, rangeMax],
                                            },
                                            rangeMin,
                                            rangeMax,
                                        ),
                                    );
                                }}
                                chipLabels={chipLabels}
                            />
                        </FlexRow>
                    </Container>
                )}
            </Stack>

            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                <FormGroup>
                    <FormLabel>{companyFilterLabels.sector}</FormLabel>
                    <InputAutocomplete name="sector" options={sectorOptions} multiple />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{companyFilterLabels.postalCode}</FormLabel>
                    <InputAutocomplete name="postalCode" options={postalCodeOptions} multiple />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{companyFilterLabels.city}</FormLabel>
                    <InputAutocomplete name="city" options={cityOptions} multiple />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{companyFilterLabels.totalJobOffersRange}</FormLabel>
                    <InputSliderRange
                        name="totalJobOffersRange"
                        min={rangeMin}
                        max={rangeMax}
                        defaultMin={filter.totalJobOffersMin ?? rangeMin}
                        defaultMax={filter.totalJobOffersMax ?? rangeMax}
                        minDistance={0}
                    />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};

export default CompanyFilter;

